import { useEffect, useMemo } from 'react';
import { useFamilyDocs, useLiveDocs } from '../../db/useScopedDocs';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, useWatch, Controller, type Resolver } from 'react-hook-form';
import { useRecordNumberAssist } from './useRecordNumberAssist';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CircularProgress from '@mui/material/CircularProgress';
import CommentIcon from '@mui/icons-material/Comment';
import Typography from '@mui/material/Typography';
import { v4 as uuid } from 'uuid';
import { useRxCollection } from 'rxdb/plugins/react';
import {
    findingInputSchema,
    isRecordNumberAllowed,
    DEFAULT_FINDING_TYPE,
    SHORT_DESCRIPTION_OPTIONS,
    INTERPRETATION_OPTIONS,
    PRELIMINARY_DATING_OPTIONS,
    DATING_BASIS_OPTIONS,
    type FindingDocType,
    type FindingType,
    type FindingPlanumDocType,
    type FindingProfileDocType
} from '@ast-data/shared';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useSavedToast } from '../../components/toastContext';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useFamilyQuotas } from '../projects/useFamilyQuotas';
import RecordedAtField from '../../components/RecordedAtField';
import { bindFormFields, labelIdsFrom } from '../../components/formFields';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useFindingSequence } from './useFindingSequence';
import {
    useForwardBackState,
    useDialogBackCrumb
} from '../../components/dialogBackTrail';
import { useFocusFieldOnOpen } from '../../components/cellFocus';
import { useNewFindingType } from './recordDialogHooks';
import FindingTypeChip from './FindingTypeChip';
import FindingDescriptionList from './FindingDescriptionList';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useFindingPdfExport } from '../export/findingPdf/useFindingPdfExport';
import { theseRecords } from '../export/pdf/exportScope';
import PdfExportProgress from '../export/findingPdf/PdfExportProgress';
import { useFindingBasePath } from './findingRefLabels';

// Tree-grid columns whose `field` differs from the Befund form input to focus.
const FINDING_CELL_FOCUS_ALIASES: Record<string, string> = {
    date: 'recordedAt'
};
import { RECOMMENDED_FINDING_FIELDS } from './descriptionValidation';
import { useAuth } from '../auth/useAuth';
import FormSectionBox from '../../components/FormSectionBox';
import FindDatingList from './FindDatingList';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useConfirm } from '../../components/confirmContext';
import {
    countDescriptionsHidingData,
    otherFindingType
} from './findingTypeSwitch';

/** Ordered sections for the in-form nav; must mirror the section boxes below. */
const FINDING_FORM_SECTIONS: FormSectionNavEntry[] = [
    { id: 'identity', titleId: 'finding.section.identity' },
    { id: 'dating', titleId: 'finding.section.dating' },
    { id: 'descriptions', titleId: 'finding.section.descriptions' }
];

/**
 * The Befund carries only identity/interpretation/dating/remark. The spatial
 * context (Flurstück/Fläche/Sondage) and the stratigraphy relations live on each
 * Planum/Profil sub-description (see FindingPlanum/ProfileFormDialog).
 */
type FormValues = {
    findingNumber: string;
    /**
     * Picked in the add menu; switched later only through the type chip. A form
     * value all the same: the zod resolver hands `onSubmit` its PARSED values,
     * so leaving it out lets the schema's default ride along and every save
     * patches a baulicher Bestand back into an ordinary Befund.
     */
    findingType: FindingType;
    shortDescription: string;
    interpretation: string;
    datingBasis: string;
    preliminaryDating: string;
    remark: string;
    onlyVisibleInProfile: boolean;
    recordedAt: string;
};

// A function (not a const) so recordedAt defaults to the moment the form opens.
function emptyValues(findingType: FindingType): FormValues {
    return {
        findingNumber: '',
        findingType,
        shortDescription: '',
        interpretation: '',
        datingBasis: '',
        preliminaryDating: '',
        remark: '',
        onlyVisibleInProfile: false,
        recordedAt: new Date().toISOString()
    };
}

function toValues(finding: FindingDocType): FormValues {
    return {
        findingNumber: finding.findingNumber,
        findingType: finding.findingType,
        shortDescription: finding.shortDescription,
        interpretation: finding.interpretation,
        datingBasis: finding.datingBasis,
        preliminaryDating: finding.preliminaryDating,
        remark: finding.remark,
        onlyVisibleInProfile: finding.onlyVisibleInProfile,
        recordedAt: finding.recordedAt
    };
}

interface FindingFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    finding?: FindingDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
}

export default function FindingFormDialog({
    open,
    onClose,
    projectId,
    finding,
    focusField
}: FindingFormDialogProps) {
    const intl = useIntl();
    const collection = useRxCollection<FindingDocType>('findings');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const { user: authUser } = useAuth();
    const isEdit = !!finding;
    // A reader opens the record to look at it: the dialog shows it as a
    // sheet and has nothing to save (see RecordFormDialog).
    const ability = useAbility();
    const readOnly = !ability.can(
        isEdit ? 'update' : 'create',
        scopedSubject('Finding', { projectId })
    );
    const canChangeType =
        isEdit && !readOnly && ability.can('update', 'FindingType');
    const { confirm } = useConfirm();

    // The type the add menu asked for while creating (carried in the route).
    const newFindingType = useNewFindingType(isEdit ? 'edit' : 'new');

    // Findings are drawn from the whole Teilmaßnahme family (linked projects
    // share Befunde), so the number-uniqueness check spans the family.
    const familyIds = useFamilyProjectIds(projectId);
    const { results: findings } = useFamilyDocs<FindingDocType>(
        'findings',
        familyIds
    );
    // The Kontingente in force — family-scoped for the same reason. Empty means
    // the family has no allocation yet, and the number stays unrestricted.
    const { findingQuotas } = useFamilyQuotas(projectId);

    const ownDescriptions = { selector: { findingId: finding?.id ?? '' } };
    const { results: planums } = useLiveDocs<FindingPlanumDocType>(
        'finding_planums',
        ownDescriptions
    );
    const { results: profiles } = useLiveDocs<FindingProfileDocType>(
        'finding_profiles',
        ownDescriptions
    );

    // Memoized: FindDatingList filters every Fund in the family against these,
    // and a fresh array each render would defeat that memo.
    const planumIds = useMemo(
        () => planums.map((planum) => planum.id),
        [planums]
    );
    const profileIds = useMemo(
        () => profiles.map((profile) => profile.id),
        [profiles]
    );

    const {
        register,
        handleSubmit,
        reset,
        setError,
        setFocus,
        setValue,
        control,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver: zodResolver(
            findingInputSchema
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues(DEFAULT_FINDING_TYPE)
    });

    const { text, suggest, richText } = bindFormFields<FormValues>({
        intl,
        readOnly,
        register,
        control,
        errors,
        validationMessage,
        labelId: labelIdsFrom('finding.field'),
        recommended: (name) => RECOMMENDED_FINDING_FIELDS.has(name)
    });

    // Read as a hook rather than through `watch()`, which the React Compiler
    // cannot memoize (see react-hooks/incompatible-library).
    const onlyVisibleInProfile = useWatch({
        control,
        name: 'onlyVisibleInProfile'
    });
    const findingNumber = useWatch({ control, name: 'findingNumber' });
    const findingType = useWatch({ control, name: 'findingType' });

    // The Befundnummern given in the family, for the hint under the field.
    const findingNumbers = useMemo(
        () =>
            findings.map((existing) => ({
                id: existing.id,
                number: existing.findingNumber
            })),
        [findings]
    );
    const numberAssist = useRecordNumberAssist({
        records: findingNumbers,
        currentId: finding?.id ?? '',
        quotas: findingQuotas,
        value: findingNumber,
        // Only while a number is being given: an existing Befund has its own.
        offer: !isEdit && !readOnly,
        onApply: (number) =>
            setValue('findingNumber', number, {
                shouldDirty: true,
                shouldValidate: true
            })
    });

    useEffect(() => {
        if (open) {
            reset(finding ? toValues(finding) : emptyValues(newFindingType));
        }
    }, [open, finding, newFindingType, reset]);

    useFocusFieldOnOpen(setFocus, open, focusField, FINDING_CELL_FOCUS_ALIASES);

    const translations = useRecordTranslations({
        ownerType: 'finding',
        ownerId: finding?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty: isDirty || translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });
    // The Befund can be REACHED from a description's breadcrumb chip, so the
    // "back" button goes through the guard (and can save on the way).
    const selfLabel = intl.formatMessage(
        { id: 'finding.crumb' },
        { number: finding?.findingNumber || '?' }
    );
    const guardedNav = useGuardedNavigate({
        isDirty: isDirty || translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(selfLabel)
    });
    const backCrumb = useDialogBackCrumb(closeGuard.requestBack);
    // Step through the Befunde by Befundnummer, as the tree lists them, with
    // each one's Plana and Profile in between (see useFindingSequence).
    const siblings = useFindingSequence({
        projectId,
        currentId: finding?.id ?? '',
        requestNavigate: guardedNav.requestNavigate
    });
    // The Befunddokumentation of THIS Befund: the grid exports a checkbox
    // selection, which the phone's card list has no room for.
    const {
        exportPdf,
        busyId: pdfBusyId,
        progress: pdfProgress,
        confirm: pdfConfirm,
        resolveConfirm: resolvePdfConfirm
    } = useFindingPdfExport();
    const exportingPdf = pdfBusyId === projectId;
    // This dialog serves the field tool as well, so its Planum/Profil links must
    // follow the app the user is in rather than always pointing at the office.
    const findingBasePath = useFindingBasePath();

    // Switched in the form and written with the save, like any other field.
    async function changeFindingType() {
        const nextType = otherFindingType(findingType);
        const hiddenCount = countDescriptionsHidingData(
            [...planums, ...profiles],
            nextType
        );
        const confirmed = await confirm({
            title: <FormattedMessage id="finding.type.change.title" />,
            message: (
                <FormattedMessage
                    id="finding.type.change.message"
                    values={{
                        next: intl.formatMessage({
                            id: `finding.type.${nextType}`
                        }),
                        hidden: hiddenCount
                    }}
                />
            ),
            confirmLabel: <FormattedMessage id="finding.type.change.confirm" />
        });
        if (!confirmed) {
            return;
        }
        setValue('findingType', nextType, { shouldDirty: true });
    }

    async function onSubmit(data: FormValues) {
        if (readOnly) {
            return;
        }
        const findingNumber = data.findingNumber.trim();
        // Once the family has a Kontingent, the number must be an integer inside
        // one of its ranges. No Kontingent = unrestricted (pre-quota behaviour).
        if (!isRecordNumberAllowed(findingQuotas, findingNumber)) {
            setError(
                'findingNumber',
                {
                    type: 'validate',
                    message: 'outsideQuota'
                },
                { shouldFocus: true }
            );
            return;
        }
        const taken = findings.some(
            (existing) =>
                existing.id !== (finding?.id ?? '') &&
                existing.findingNumber.trim().toLowerCase() ===
                    findingNumber.toLowerCase()
        );
        if (taken) {
            setError(
                'findingNumber',
                {
                    type: 'validate',
                    message: 'duplicateNumber'
                },
                { shouldFocus: true }
            );
            return;
        }

        const now = new Date().toISOString();
        const content = { ...data, findingNumber };

        const savedId = isEdit ? finding!.id : uuid();
        if (isEdit && collection) {
            const doc = await collection.findOne(savedId).exec();
            if (doc) {
                // The kind only when switched here. The form holds it as it
                // was on opening, and saving a remark must not undo a switch
                // made elsewhere since — nor, for whoever may not switch, have
                // the save refused.
                const { findingType, ...fields } = content;
                const switched = findingType !== finding?.findingType;
                await doc.incrementalPatch({
                    ...fields,
                    ...(switched ? { findingType } : {}),
                    updatedAt: now
                });
            }
        } else if (collection) {
            await collection.insert({
                id: savedId,
                projectId,
                ...content,
                // Stamp the creator locally so the Befund's Verfasser shows
                // immediately (offline too); the server stamps the same on push.
                createdBy: authUser?.id ?? '',
                updatedBy: authUser?.id ?? '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(savedId);
        notifySaved();
        // Close, or go to a pending "save and go back" target (a routed dialog
        // also steps over the guard's history sentinel).
        guardedNav.finishSubmit();
    }

    // Only for a SAVED Befund: the export reads the stored record, and a new
    // one has nothing to read.
    const pdfButton = isEdit && finding && (
        <Button
            variant="outlined"
            color="inherit"
            startIcon={
                exportingPdf ? (
                    <CircularProgress size={18} color="inherit" />
                ) : (
                    <PictureAsPdfIcon />
                )
            }
            disabled={exportingPdf}
            onClick={() =>
                void exportPdf(theseRecords([finding.id]), projectId)
            }
            sx={{ mr: 'auto' }}
        >
            <FormattedMessage id="findingPdf.export.one" />
        </Button>
    );

    // Both belong to the export hook: without the confirmation the promise
    // behind the size warning never settles and the export hangs — even though
    // a single Befund should never reach that threshold.
    const pdfDialogs = (
        <>
            <PdfExportProgress progress={pdfProgress} />
            <ConfirmDialog
                open={!!pdfConfirm}
                title={<FormattedMessage id="findingPdf.confirm.title" />}
                message={
                    <FormattedMessage
                        id="findingPdf.confirm.message"
                        values={{
                            findings: pdfConfirm?.findings ?? 0,
                            descriptions: pdfConfirm?.descriptions ?? 0
                        }}
                    />
                }
                confirmLabel={
                    <FormattedMessage id="findingPdf.confirm.action" />
                }
                onConfirm={() => resolvePdfConfirm(true)}
                onCancel={() => resolvePdfConfirm(false)}
            />
        </>
    );

    return (
        <RecordFormDialog
            open={open}
            sections={FINDING_FORM_SECTIONS}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            footerStartSlot={pdfButton}
            extraDialogs={pdfDialogs}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    backCrumb={backCrumb}
                    siblings={siblings}
                    title={
                        isEdit && finding?.findingNumber ? (
                            <FormattedMessage
                                id="finding.form.editTitleNumber"
                                values={{ number: finding.findingNumber }}
                            />
                        ) : (
                            <FormattedMessage
                                id={
                                    isEdit
                                        ? 'finding.form.editTitle'
                                        : 'finding.form.newTitle'
                                }
                            />
                        )
                    }
                />
            }
        >
            <FormSectionBox
                titlePrefix="finding.section"
                id="identity"
                // The same chip the Planum/Profil forms carry in their own
                // Kenndaten heading (see FindingTypeChip).
                titleSlot={
                    <FindingTypeChip
                        findingType={findingType}
                        onChange={
                            canChangeType
                                ? () => void changeFindingType()
                                : undefined
                        }
                    />
                }
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: {
                            xs: 'column',
                            sm: 'row'
                        }
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        {text('findingNumber', {
                            // The button under it fills it from outside.
                            shrinkLabel: findingNumber !== '',
                            ...numberAssist
                        })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <RecordedAtField control={control} name="recordedAt" />
                    </Box>
                </Box>
                {suggest('shortDescription', SHORT_DESCRIPTION_OPTIONS)}
                {richText('remark', { icon: <CommentIcon /> })}
            </FormSectionBox>
            <FormSectionBox titlePrefix="finding.section" id="dating">
                {suggest('interpretation', INTERPRETATION_OPTIONS)}
                {suggest('datingBasis', DATING_BASIS_OPTIONS)}
                {suggest('preliminaryDating', PRELIMINARY_DATING_OPTIONS)}
                {/* What the Funde in this Befund are dated to, for the person
                                    deciding the line above. A Fund hangs off the
                                    Befund's id, so there is nothing to show
                                    before the first save. */}
                {isEdit && finding && (
                    <FindDatingList
                        projectId={projectId}
                        findingId={finding.id}
                        findingNumber={finding.findingNumber}
                        planumIds={planumIds}
                        profileIds={profileIds}
                        onNavigate={guardedNav.requestNavigate}
                    />
                )}
            </FormSectionBox>
            <FormSectionBox titlePrefix="finding.section" id="descriptions">
                {isEdit && finding ? (
                    <>
                        {/* The two halves of one rule: a Befund that never
                                        showed in plan has no Planum, so the flag
                                        is locked once one exists and adding one
                                        is barred while it is set. */}
                        <Controller
                            control={control}
                            name="onlyVisibleInProfile"
                            render={({ field }) => (
                                <Box sx={{ mb: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value}
                                                disabled={planums.length > 0}
                                                onChange={(event) =>
                                                    field.onChange(
                                                        event.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label={intl.formatMessage({
                                            id: 'finding.field.onlyVisibleInProfile'
                                        })}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        <FormattedMessage
                                            id={
                                                planums.length > 0
                                                    ? 'finding.onlyVisibleInProfile.hasPlanum'
                                                    : 'finding.onlyVisibleInProfile.hint'
                                            }
                                        />
                                    </Typography>
                                </Box>
                            )}
                        />
                        <FindingDescriptionList
                            projectId={projectId}
                            planums={planums}
                            profiles={profiles}
                            basePath={findingBasePath(projectId, finding.id)}
                            onNavigate={guardedNav.requestNavigate}
                            allowPlanums={!onlyVisibleInProfile}
                        />
                    </>
                ) : (
                    // Descriptions hang off the Befund's id — it exists
                    // only after the first save.
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage id="finding.descriptions.saveFirst" />
                    </Typography>
                )}
            </FormSectionBox>
        </RecordFormDialog>
    );
}
